import React, { useEffect, useState } from 'react'

import {
  fetchServices,
  fetchUser,
  patchUser,
} from '../../../../../api/commonApi'
import { FileTypeSelector } from '@features/settings/components/FileTypeSelector'
import type {
  AgentService,
  FileTypeInput,
  SettingsUser,
} from '@features/settings/types'
import { normalizeUserFileTypes } from '@features/settings/utils/fileTypeUtils'
import { getStoredUserId } from '@features/settings/utils'

const ConfigureTrackingStep = () => {
  const [fileTypes, setFileTypes] = useState<FileTypeInput[]>([])
  const [userInfo, setUserInfo] = useState<SettingsUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [updatingFileTypes, setUpdatingFileTypes] = useState(false)
  const userId = getStoredUserId()

  const updateUserFileTypes = async (nextFileTypes: FileTypeInput[]) => {
    if (!userInfo?.id || updatingFileTypes) return

    const normalizedFileTypes = normalizeUserFileTypes(nextFileTypes)
    const previousUserInfo = userInfo
    const settings = {
      ...(userInfo.settings ?? {}),
      fileTypes: normalizedFileTypes,
    }

    setUpdatingFileTypes(true)
    setUserInfo({ ...userInfo, settings })
    try {
      await patchUser(userInfo.id, { settings })
    } catch (error) {
      setUserInfo(previousUserInfo)
      console.error('Failed to update user file types', error)
    } finally {
      setUpdatingFileTypes(false)
    }
  }

  useEffect(() => {
    let active = true

    const loadFileTypes = async () => {
      if (!userId) {
        console.error('Failed to load user file types: userId not found')
        if (active) setLoading(false)
        return
      }

      try {
        const [services, user] = await Promise.all([
          fetchServices() as Promise<AgentService[]>,
          fetchUser(userId) as Promise<SettingsUser | null>,
        ])
        if (!active) return

        const agent = (services ?? []).find(service => service.key === 'agent')
        setFileTypes(agent?.settings?.fileTypes ?? [])
        setUserInfo(user)
      } catch (error) {
        console.error('Failed to load user file types', error)
      } finally {
        if (active) setLoading(false)
      }
    }

    void loadFileTypes()

    return () => {
      active = false
    }
  }, [userId])

  return (
    <div className="max-w-[900px]">
      <h2 className="text-lg font-semibold text-black">
        Configure What You Track
      </h2>
      <p className="mt-3 max-w-[880px] text-base leading-[1.35] text-[#666]">
        Before getting started with the Desktop Agent, let&apos;s configure
        which file types MIRA should track. This allows you to tailor activity
        tracking to your work preferences. You can change these settings at any
        time from the Settings page in the side menu.
      </p>
      <div className="mt-5">
        {loading ? (
          <div
            className="flex animate-pulse flex-col gap-5 rounded-lg bg-white p-5 shadow-[0_4px_18px_0_rgba(119,124,139,0.20)]"
            aria-label="Loading file types"
            role="status"
          >
            <div className="flex flex-col gap-2">
              <div className="h-4 w-64 rounded bg-[#ebebeb]" />
              <div className="flex min-h-[84px] flex-wrap items-center gap-2 rounded-md border border-[#e5e5e5] px-2.5 py-2">
                {[20, 24, 16, 28, 20].map((width, index) => (
                  <div
                    key={`${width}-${index}`}
                    className="h-8 rounded-full bg-[#ebebeb]"
                    style={{ width: `${width * 4}px` }}
                  />
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <div className="h-4 w-40 rounded bg-[#ebebeb]" />
              <div className="flex min-h-[52px] flex-wrap items-center gap-2 px-2.5 py-2">
                {[16, 24, 20, 28, 16, 20].map((width, index) => (
                  <div
                    key={`${width}-${index}`}
                    className="h-8 rounded-full bg-[#ebebeb]"
                    style={{ width: `${width * 4}px` }}
                  />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <FileTypeSelector
            fileTypes={fileTypes}
            selectedFileTypes={userInfo?.settings?.fileTypes ?? []}
            disabled={updatingFileTypes || !userInfo}
            onChange={nextFileTypes => void updateUserFileTypes(nextFileTypes)}
          />
        )}
      </div>
      <div className="mt-5 flex items-start gap-3 rounded border border-[#d9d9d9] bg-[#f5f5f5] px-4 py-3 text-sm leading-[1.35] text-[#666]">
        <span aria-hidden="true" className="text-amber-500">
          ▲
        </span>
        <span>
          If you&apos;ve already signed in to the Desktop Agent, please sign out
          and sign back in, or turn tracking off and back on, to apply the file
          type changes.
        </span>
      </div>
    </div>
  )
}

export default ConfigureTrackingStep
